import { useEffect, useState } from "react";
import { keyFromFragment } from "../artifact/crypto";
import { openArtifact, type ArtifactResult } from "../artifact/transit";

const FAILURE: Record<string, string> = {
  no_key: "This link has no key for the picture, so it cannot be shown. Ask for the full link (everything after the #).",
  decrypt_failed: "The picture could not be decrypted with this link's key: the key is wrong, or the stored file was altered. Do not rely on it.",
  hash_mismatch: "The picture does not match the claim: it is not the file the holder signed. Do not rely on it.",
  gone: "This link has been used or has expired, so its picture is gone.",
  none: "The picture is no longer stored (it expired, or was never uploaded for this link).",
  fetch_failed: "The picture could not be fetched. Try again in a moment.",
};

/**
 * The claim's picture, for the attester: this request's ciphertext fetched with the token,
 * decrypted on this device with the key from the fragment, and checked against the signed
 * artifact_hash. Mounted only after who is asking and for what are on screen. Nothing is
 * uploaded; the decrypted file lives only in this page's memory.
 */
export function ArtifactPanel({ token }: { token: string }) {
  const [res, setRes] = useState<(ArtifactResult & { mime?: string | null }) | null>(null);
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true, made = "";
    // Read once from the fragment; kept in memory only.
    const key = keyFromFragment(window.location.hash);
    openArtifact(token, key).then((r) => {
      if (!alive) return;
      setRes(r);
      if (r.ok) { made = URL.createObjectURL(new Blob([r.plain], { type: r.mime ?? "application/octet-stream" })); setUrl(made); }
    });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [token]);

  if (!res) return <div className="card artifact" data-testid="artifact-loading"><span className="hint">Decrypting the picture on this device…</span></div>;
  if (!res.ok) return <div className="card artifact"><p className="error" role="alert" data-testid="artifact-failed">{FAILURE[res.reason]}</p></div>;
  return (
    <div className="card artifact">
      <div className="artifact-mark" data-testid="artifact-verified">✓ matches the claim</div>
      {res.mime === "application/pdf"
        ? <object data={url} type="application/pdf" className="artifact-pdf" data-testid="artifact-pdf" aria-label="The claim's PDF">The PDF could not be shown here.</object>
        : <img src={url} alt="The picture attached to the claim" className="artifact-img" data-testid="artifact-image" />}
      <div className="hint">Decrypted and checked on this device. It was never sent anywhere in the clear.</div>
    </div>
  );
}
